import { z } from 'zod';

export const productCategorySchema = z.enum([
  'FIORE',
  'OLIO_FOOD',
  'OLIO_COSMETIC',
  'DERIVATO',
  'SCARTO',
  'ALTRO',
]);
export type ProductCategory = z.infer<typeof productCategorySchema>;

export const productStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'ARCHIVED']);
export type ProductStatus = z.infer<typeof productStatusSchema>;

// ISO 3166-1 alpha-2 (2 lettere maiuscole)
const countryCodeSchema = z
  .string()
  .length(2, 'Codice paese deve essere 2 lettere ISO')
  .regex(/^[A-Z]{2}$/, 'Codice paese deve essere maiuscolo (es. IT, CZ, DE)');

// Body schemas (input)

export const productCreateRequestSchema = z.object({
  code: z
    .string()
    .min(1, 'Codice obbligatorio')
    .max(64, 'Codice max 64 caratteri')
    .regex(/^[A-Z0-9_-]+$/i, 'Codice puo contenere solo lettere, numeri, _ e -'),
  name: z.string().min(1, 'Nome obbligatorio').max(200),
  category: productCategorySchema.default('ALTRO'),
  unit: z.string().min(1).max(10).default('g'),
  vatRate: z.coerce.number().min(0).max(100).default(0),
  thcThreshold: z.coerce.number().min(0).max(100).nullable().optional(),
  countriesSale: z.array(countryCodeSchema).default([]),
  status: productStatusSchema.default('ACTIVE'),
  notes: z.string().max(2000).nullable().optional(),
});
export type ProductCreateRequest = z.infer<typeof productCreateRequestSchema>;

export const productUpdateRequestSchema = productCreateRequestSchema.partial();
export type ProductUpdateRequest = z.infer<typeof productUpdateRequestSchema>;

// Response schemas (output)

// vatRate e thcThreshold sono Decimal sul DB. In rete li serializziamo come number.
export const productResponseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: productCategorySchema,
  unit: z.string(),
  vatRate: z.number(),
  thcThreshold: z.number().nullable(),
  countriesSale: z.array(z.string()),
  status: productStatusSchema,
  notes: z.string().nullable(),
  // ID numerico legacy dal foglio Drive LEGEND (es. 1=AFGHAN GOLD, 21=HQ CBD BOOST).
  // Null per prodotti placeholder (DA_CATEGORIZZARE) o nuovi non in LEGEND.
  driveLegendId: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProductResponse = z.infer<typeof productResponseSchema>;

export const productListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  search: z.string().trim().min(1).max(100).optional(),
  category: productCategorySchema.optional(),
  status: productStatusSchema.optional(),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

export const productListResponseSchema = z.object({
  items: z.array(productResponseSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type ProductListResponse = z.infer<typeof productListResponseSchema>;

// Enums helpers per UI (label IT)

export const PRODUCT_CATEGORY_LABELS_IT: Record<ProductCategory, string> = {
  FIORE: 'Fiore',
  OLIO_FOOD: 'Olio alimentare',
  OLIO_COSMETIC: 'Olio cosmetico',
  DERIVATO: 'Derivato',
  SCARTO: 'Scarto',
  ALTRO: 'Altro',
};

export const PRODUCT_STATUS_LABELS_IT: Record<ProductStatus, string> = {
  ACTIVE: 'Attivo',
  INACTIVE: 'Inattivo',
  ARCHIVED: 'Archiviato',
};
